import type { ReactElement } from "react";

import { useDispatch } from "react-redux";

import { colorsPalettes } from "@similarweb/styles";
import { IconButton, Tooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import trackingService from "app/pages/sales-intelligence/sub-modules/contacts-search/services/tracking/trackingService";
import { contactIssueModalToggledAction } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/action-creators";
import type { ContactSearchEntry } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import { isContactNameHidden } from "app/pages/sales-intelligence/sub-modules/contacts-search/utils/isContactNameHidden";

import { StyledBaseCell, StyledContactNameText, StyledReportIconButton } from "./styles";

interface IContactNameCellProps {
    value: string;
    rowData: ContactSearchEntry;
    withLink?: boolean;
}

const ContactNameCell = ({ value, rowData, withLink = true }: IContactNameCellProps): ReactElement => {
    const dispatch = useDispatch();
    const { linkedin, hasIssues } = rowData;

    const isValueHidden = isContactNameHidden(value);

    const handleReportClick = () => {
        trackingService.trackReportIssueOpenModal();
        dispatch(contactIssueModalToggledAction({ contact: rowData }));
    };

    const reportTooltipText = hasIssues
        ? translate("si.contacts_search.contacts.report.tooltip.reported")
        : translate("si.contacts_search.contacts.report.tooltip");

    return (
        <StyledBaseCell dataAutomation="contacts-table.cell.contacts-name">
            <StyledContactNameText
                data-automation="contacts-name-cell.title"
                fontVariant="input2"
                color={colorsPalettes.carbon[400]}
                isBlurred={isValueHidden}
            >
                {value}
            </StyledContactNameText>

            {linkedin && !isValueHidden && withLink ? (
                <Tooltip content={translate("si.contacts_search.contacts.linked_in.tooltip")}>
                    <a
                        href={linkedin}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-automation="contacts-name-cell.link.linkedin"
                    >
                        <IconButton
                            className="visibleOnRowHover"
                            type="flat"
                            iconName="linkedin-colored"
                            iconColor={colorsPalettes.blue[400]}
                            iconSize="xs"
                            dataAutomation="contacts-name-cell.button.linkedin"
                            onClick={() => trackingService.trackLinkedInClick()}
                        />
                    </a>
                </Tooltip>
            ) : null}

            {!isValueHidden ? (
                <Tooltip content={reportTooltipText}>
                    <StyledReportIconButton
                        className="visibleOnRowHover"
                        iconName="flag"
                        iconSize="xs"
                        type="flat"
                        hasIssues={hasIssues}
                        dataAutomation="contacts-name-cell.button.report"
                        onClick={handleReportClick}
                    />
                </Tooltip>
            ) : null}
        </StyledBaseCell>
    );
};

export default ContactNameCell;
